import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent } from "@/components/ui/card";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import ImageUploader from "@/components/admin/ImageUploader";

const BADGES = ["New drop", "Limited", "Bestseller", "Restock"];

export default function ProductForm() {
    const { id } = useParams();
    const editing = !!id;
    const navigate = useNavigate();
    const [categories, setCategories] = useState([]);
    const [data, setData] = useState({
        title: "", slug: "", description: "", category: "Merchandise", price: 0, compare_price: null, is_event_package: false,
        images: [""], video_url: "", badges: [], featured: false, is_new: true, is_published: true,
        tags: [], variants: [{ sku: "", size: "Standard package", color: "Event", color_hex: "#073d31", stock: 20, low_stock_threshold: 2 }],
    });

    useEffect(() => {
        // Load dynamic categories
        api.get("/categories").then((r) => {
            const activeCategories = (r.data || []).filter((category) => category.is_active !== false);
            const fallbackCategories = ["Merchandise", "Event supplies", "Corporate events", "Weddings", "Exhibitions", "Catering", "MICE & conferences", "Promotions", "Other"].map((name) => ({ slug: name.toLowerCase().replace(/[^a-z0-9]+/g, "-"), name }));
            const list = [...new Map([...fallbackCategories, ...activeCategories].map((category) => [category.name.toLowerCase(), category])).values()];
            setCategories(list);
            if (list.length && !editing) {
                setData((d) => ({ ...d, category: d.category || "Merchandise" }));
            }
        }).catch(() => setCategories(["Merchandise", "Event supplies", "Corporate events", "Weddings", "Exhibitions", "Catering", "MICE & conferences", "Promotions", "Other"].map((name) => ({ slug: name.toLowerCase().replace(/[^a-z0-9]+/g, "-"), name }))));
    }, [editing]);

    useEffect(() => {
        if (!editing) return;
        api.get("/admin/products").then(r => {
            const p = r.data.find(x => x.id === id);
            if (p) setData(p);
        });
    }, [id, editing]);

    const submit = async (e) => {
        e.preventDefault();
        try {
            const payload = { ...data, price: Number(data.price), compare_price: data.compare_price ? Number(data.compare_price) : null };
            payload.images = (data.images || []).filter(Boolean);
            payload.variants = (data.variants || []).map((v, index) => ({ ...v, sku: v.sku.trim() || `${payload.slug.toUpperCase().replace(/[^A-Z0-9]+/g, "-")}-${String(index + 1).padStart(2, "0")}`, stock: Number(v.stock || 0), low_stock_threshold: Number(v.low_stock_threshold || 2) }));
            if (editing) {
                await api.put(`/admin/products/${id}`, payload);
                toast.success("Product updated");
            } else {
                await api.post("/admin/products", payload);
                toast.success("Product created");
            }
            navigate("/admin/products");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Save failed");
        }
    };

    const update = (k, v) => setData(d => ({ ...d, [k]: v }));
    const updateImage = (i, v) => setData(d => ({ ...d, images: d.images.map((x, idx) => idx === i ? v : x) }));
    const addImage = () => setData(d => ({ ...d, images: [...d.images, ""] }));
    const removeImage = (i) => setData(d => ({ ...d, images: d.images.filter((_, idx) => idx !== i) }));
    const updateVariant = (i, k, v) => setData(d => ({ ...d, variants: d.variants.map((x, idx) => idx === i ? { ...x, [k]: v } : x) }));
    const addVariant = () => setData(d => ({ ...d, variants: [...d.variants, { sku: "", size: "M", color: "Onyx", color_hex: "#121214", stock: 0, low_stock_threshold: 5 }] }));
    const removeVariant = (i) => setData(d => ({ ...d, variants: d.variants.filter((_, idx) => idx !== i) }));

    return (
        <form onSubmit={submit} className="max-w-4xl" data-testid="admin-product-form">
            <h1 className="font-display text-4xl">{editing ? (data.is_event_package ? "Edit event package" : "Edit product") : (data.is_event_package ? "New event package" : "New product")}</h1>

            <Card className="mt-6 rounded-[20px] border-[#E7DED2] bg-white/80">
                <CardContent className="p-5 flex items-center justify-between gap-5">
                    <div><Label className="font-semibold">{data.is_event_package ? "This is an event package" : "This is a standard product"}</Label><p className="mt-1 text-xs text-muted-foreground">{data.is_event_package ? "Shown with event experiences and proposal/booking options." : "Shown in the all-products catalog. Switch on to list as an event package."}</p></div>
                    <Switch checked={!!data.is_event_package} onCheckedChange={(value) => setData((current) => ({ ...current, is_event_package: value, category: value ? (/event|wedding|corporate|exhibition|catering|mice|promotion/i.test(current.category) ? current.category : "Corporate events") : (/event|wedding|corporate|exhibition|catering|mice|promotion/i.test(current.category) ? "Merchandise" : current.category) }))} aria-label="Mark as an event package" />
                </CardContent>
            </Card>

            <Card className="mt-6 rounded-[20px] border-[#E7DED2] bg-white/80">
                <CardContent className="p-6 space-y-4">
                    <div className="grid sm:grid-cols-2 gap-4">
                        <Fld label="Title" required>
                            <Input required value={data.title} onChange={e => update("title", e.target.value)} data-testid="admin-product-title-input" />
                        </Fld>
                        <Fld label="Slug" required>
                            <Input required value={data.slug} onChange={e => update("slug", e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-"))} data-testid="admin-product-slug-input" />
                        </Fld>
                    </div>
                    <Fld label="Description">
                        <Textarea rows={3} value={data.description} onChange={e => update("description", e.target.value)} />
                    </Fld>
                    <div className="grid sm:grid-cols-3 gap-4">
                        <Fld label="Category">
                            <Select value={data.category} onValueChange={v => update("category", v)}>
                                <SelectTrigger className="rounded-2xl bg-white border-[#E7DED2]"><SelectValue /></SelectTrigger>
                                <SelectContent>{categories.map(c => <SelectItem key={c.id || c.slug} value={c.name}>{c.name}</SelectItem>)}</SelectContent>
                            </Select>
                        </Fld>
                        <Fld label="Starting price (paise; ₹25,000 = 2500000)">
                            <Input type="number" value={data.price} onChange={e => update("price", e.target.value)} />
                        </Fld>
                        <Fld label="Compare price">
                            <Input type="number" value={data.compare_price || ""} onChange={e => update("compare_price", e.target.value)} />
                        </Fld>
                    </div>
                </CardContent>
            </Card>

            <Card className="mt-6 rounded-[20px] border-[#E7DED2] bg-white/80">
                <CardContent className="p-6">
                    <h3 className="font-display text-2xl">Images</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
                        {data.images.map((url, i) => (
                            <div key={i} className="relative">
                                <ImageUploader
                                    label={`Image ${i + 1}`}
                                    value={url}
                                    onChange={(v) => updateImage(i, v)}
                                />
                                <button
                                    type="button"
                                    onClick={() => removeImage(i)}
                                    className="absolute -top-2 -right-2 h-7 w-7 rounded-full bg-white border border-[#E7DED2] hover:bg-red-50 hover:text-red-600 flex items-center justify-center shadow-sm"
                                    title="Remove image slot"
                                >
                                    <Trash2 className="h-3.5 w-3.5" />
                                </button>
                            </div>
                        ))}
                    </div>
                    <button
                        type="button"
                        onClick={addImage}
                        className="mt-4 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white border border-[#E7DED2] hover:bg-[#FFFCF7] text-xs font-mono-ui"
                    >
                        <Plus className="h-3.5 w-3.5" /> Add image slot
                    </button>
                </CardContent>
            </Card>

            <Card className="mt-6 rounded-[20px] border-[#E7DED2] bg-white/80">
                <CardContent className="p-6">
                    <h3 className="font-display text-2xl">Product video</h3>
                    <p className="text-xs text-[#857C72] mt-1">Optional. MP4/WebM up to 80 MB.</p>
                    <div className="mt-3">
                        <ImageUploader
                            label="Video"
                            value={data.video_url}
                            onChange={(v) => update("video_url", v)}
                            accept="video/*"
                            kind="video"
                            maxMB={80}
                        />
                    </div>
                </CardContent>
            </Card>

            <Card className="mt-6 rounded-[20px] border-[#E7DED2] bg-white/80">
                <CardContent className="p-6">
                    <h3 className="font-display text-2xl">{data.is_event_package ? "Booking options & availability" : "Variants & inventory"}</h3>
                    <p className="text-xs text-muted-foreground mt-1">{data.is_event_package ? "Availability is the number of bookable packages. Price is entered in paise." : "Track stock for this product. Price is entered in paise."}</p>
                    <div className="mt-3 space-y-3">
                        {data.variants.map((v, i) => (
                            <div key={i} className="grid grid-cols-2 sm:grid-cols-6 gap-2 items-end p-3 rounded-2xl bg-[#FAF7F1]">
                                <Input value={v.sku} onChange={e => updateVariant(i, "sku", e.target.value)} placeholder="Auto SKU" className="rounded-xl bg-white" />
                                <Input value={v.size} onChange={e => updateVariant(i, "size", e.target.value)} placeholder="Size" className="rounded-xl bg-white" />
                                <Input value={v.color} onChange={e => updateVariant(i, "color", e.target.value)} placeholder="Color" className="rounded-xl bg-white" />
                                <Input type="color" value={v.color_hex} onChange={e => updateVariant(i, "color_hex", e.target.value)} className="rounded-xl bg-white h-10" />
                                <Input type="number" value={v.stock} onChange={e => updateVariant(i, "stock", e.target.value)} placeholder="Stock" className="rounded-xl bg-white" />
                                <button type="button" onClick={() => removeVariant(i)} className="h-9 w-9 rounded-full bg-white border border-[#E7DED2] flex items-center justify-center"><Trash2 className="h-3.5 w-3.5" /></button>
                            </div>
                        ))}
                        <button type="button" onClick={addVariant} className="text-xs font-mono-ui flex items-center gap-1"><Plus className="h-3.5 w-3.5" /> Add variant</button>
                    </div>
                </CardContent>
            </Card>

            <Card className="mt-6 rounded-[20px] border-[#E7DED2] bg-white/80">
                <CardContent className="p-6 space-y-4">
                    <h3 className="font-display text-2xl">Visibility & flags</h3>
                    <div className="flex items-center justify-between">
                        <Label>Published</Label>
                        <Switch checked={data.is_published} onCheckedChange={v => update("is_published", v)} />
                    </div>
                    <div className="flex items-center justify-between">
                        <Label>Featured</Label>
                        <Switch checked={data.featured} onCheckedChange={v => update("featured", v)} />
                    </div>
                    <div className="flex items-center justify-between">
                        <Label>New arrival</Label>
                        <Switch checked={data.is_new} onCheckedChange={v => update("is_new", v)} />
                    </div>
                    <Fld label="Badges (comma-separated)">
                        <Input value={(data.badges || []).join(", ")} onChange={e => update("badges", e.target.value.split(",").map(s => s.trim()).filter(Boolean))} placeholder={BADGES.join(", ")} />
                    </Fld>
                </CardContent>
            </Card>

            <div className="mt-6 flex gap-3">
                <button type="submit" className="dw-btn-primary px-7 py-3 font-mono-ui" data-testid="admin-product-save">{editing ? "Save package" : "Create package"}</button>
                <button type="button" onClick={() => navigate("/admin/products")} className="dw-btn-secondary px-7 py-3 font-mono-ui">Cancel</button>
            </div>
        </form>
    );
}

function Fld({ label, children, required }) {
    return (
        <div>
            <Label className="font-mono-ui mb-1.5 block">{label}{required && " *"}</Label>
            {children}
        </div>
    );
}
